/**
 * QuickChecksPanel — Reusable evidence-gathering tools for Investigation & ECDD
 *
 * Features:
 *   - ABN Lookup (live API call, results inline)
 *   - ASIC Company Search (link out with pre-filled name)
 *   - AFSA Bankruptcy Register (link out)
 *   - Google / Media Search (link out)
 *   - Property Title Search (link out per state)
 *   - DFAT Sanctions (link out)
 *   - Each check is logged to case_activities for audit trail
 *   - Previous checks shown at bottom
 */
import React, { useState, useEffect, useCallback } from 'react'
import {
  Search, Building2, AlertTriangle, Globe, MapPin, Shield,
  ExternalLink, CheckCircle2, XCircle, Clock, ChevronDown, ChevronRight,
  Loader2, FileText,
} from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../../config/api'

const P = { pri: '#0a5169', priM: '#e8f2f6' }

const CHECK_TYPES = {
  abn_lookup: { label: 'ABN Lookup', icon: Building2, color: '#0a5169', desc: 'Australian Business Register — verify a business exists' },
  asic_search: { label: 'ASIC Company Search', icon: FileText, color: '#7c3aed', desc: 'Directors, shareholders, registration status' },
  bankruptcy: { label: 'Bankruptcy Register', icon: AlertTriangle, color: '#dc2626', desc: 'AFSA — check if individual is bankrupt' },
  media_search: { label: 'Media / Google Search', icon: Globe, color: '#0891b2', desc: 'Open-source adverse media and background' },
  property: { label: 'Property Title Search', icon: MapPin, color: '#059669', desc: 'State land registry — verify property ownership' },
  dfat_sanctions: { label: 'DFAT Sanctions List', icon: Shield, color: '#dc2626', desc: 'Australian sanctions consolidated list' },
}

function Badge({ color, bg, border, children }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: bg, color, border: border ? `1px solid ${border}` : undefined }}>
      {children}
    </span>
  )
}

function AbnLookupTool({ patronName, onLog }) {
  const [abn, setAbn] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  const lookup = async () => {
    const clean = abn.replace(/\s/g, '')
    if (!/^\d{11}$/.test(clean)) { setError('ABN must be 11 digits'); return }
    setLoading(true); setError(null); setResult(null)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/checks/abn/${clean}`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Lookup failed')
      setResult(json.data)
      // Log to case
      onLog({
        check_type: 'abn_lookup',
        search_query: clean,
        result_summary: json.data
          ? `${json.data.entityName} — ${json.data.status}${json.data.gst ? ' (GST registered)' : ''}`
          : 'ABN not found',
        result_data: json.data,
      })
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input value={abn} onChange={e => setAbn(e.target.value)} placeholder="Enter 11-digit ABN"
          className="flex-1 text-sm border border-slate-200 rounded-md px-3 py-2 font-mono"
          onKeyDown={e => e.key === 'Enter' && lookup()} />
        <button type="button" onClick={lookup} disabled={loading}
          className="px-4 py-2 text-xs font-bold text-white rounded-md border-none cursor-pointer disabled:opacity-50"
          style={{ background: P.pri }}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : 'Lookup'}
        </button>
      </div>
      {error && <div className="text-xs text-red-600 bg-red-50 rounded-md p-2 border border-red-200">{error}</div>}
      {result && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 text-sm space-y-1.5">
          {result.entityName ? (
            <>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-green-600 flex-shrink-0" />
                <span className="font-bold text-slate-800">{result.entityName}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mt-2">
                <div><span className="text-slate-400">ABN:</span> <span className="font-mono">{result.abn}</span></div>
                <div><span className="text-slate-400">Type:</span> {result.entityType}</div>
                <div><span className="text-slate-400">Status:</span>
                  <Badge color={result.status === 'Active' ? '#16a34a' : '#dc2626'}
                    bg={result.status === 'Active' ? '#f0fdf4' : '#fef2f2'}>{result.status}</Badge>
                </div>
                <div><span className="text-slate-400">GST:</span> {result.gst || '—'}</div>
                <div><span className="text-slate-400">State:</span> {result.state || '—'}</div>
                <div><span className="text-slate-400">Postcode:</span> {result.postcode || '—'}</div>
              </div>
              {result.businessNames?.length > 0 && (
                <div className="text-xs text-slate-500 mt-1">
                  <span className="text-slate-400">Trading as:</span> {result.businessNames.join(', ')}
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 text-amber-700">
              <XCircle size={14} /> ABN not found in the Australian Business Register
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function LinkOutTool({ checkType, patronName, onLog }) {
  const [searched, setSearched] = useState(false)
  const [notes, setNotes] = useState('')

  const encodedName = encodeURIComponent(patronName || '')

  const links = {
    asic_search: `https://connectonline.asic.gov.au/RegistrySearch/faces/landing/SearchRegisters.jspx?_adf.ctrl-state=search&query=${encodedName}`,
    bankruptcy: `https://www.afsa.gov.au/online-services/search-npii?search=${encodedName}`,
    media_search: `https://www.google.com/search?q=${encodedName}+%22money+laundering%22+OR+%22fraud%22+OR+%22charged%22+OR+%22convicted%22`,
    property: `https://www.nswlrs.com.au/`,
    dfat_sanctions: `https://www.dfat.gov.au/international-relations/security/sanctions/consolidated-list`,
  }

  const url = links[checkType]
  const meta = CHECK_TYPES[checkType]

  const handleOpen = () => {
    window.open(url, '_blank', 'noopener')
    setSearched(true)
  }

  const handleSaveResult = () => {
    onLog({
      check_type: checkType,
      search_query: patronName,
      result_summary: notes.trim() || `${meta.label} search performed — no notable findings`,
      result_data: { url, searched_at: new Date().toISOString() },
    })
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">{meta.desc}</div>
      <div className="flex gap-2">
        <button type="button" onClick={handleOpen}
          className="flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-md border cursor-pointer"
          style={{ borderColor: meta.color, color: meta.color, background: meta.color + '08' }}>
          <ExternalLink size={13} /> Open {meta.label}
        </button>
        {patronName && (
          <div className="flex items-center text-xs text-slate-400">
            Searching for: <span className="font-semibold text-slate-600 ml-1">{patronName}</span>
          </div>
        )}
      </div>
      {searched && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-3">
          <div className="text-xs font-semibold text-slate-600 mb-1.5">Record Result</div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="What did you find? Record the outcome of this check..."
            className="w-full min-h-[60px] text-sm border border-slate-200 rounded-md p-2.5 resize-y box-border mb-2" />
          <button type="button" onClick={handleSaveResult}
            className="px-4 py-1.5 text-xs font-bold text-white rounded-md border-none cursor-pointer"
            style={{ background: P.pri }}>
            Save to Case Record
          </button>
        </div>
      )}
    </div>
  )
}

export default function QuickChecksPanel({ caseId, patronName, memberId }) {
  const [activeCheck, setActiveCheck] = useState(null)
  const [checkLogs, setCheckLogs] = useState([])
  const [logsExpanded, setLogsExpanded] = useState(false)
  const [logsLoading, setLogsLoading] = useState(false)

  const loadLogs = useCallback(async () => {
    if (!caseId) return
    try {
      setLogsLoading(true)
      const res = await authFetch(getLocalApiUrl(`/api/checks/log/${encodeURIComponent(caseId)}`))
      const json = await res.json()
      if (json.success) setCheckLogs(json.data || [])
    } catch (_) {}
    finally { setLogsLoading(false) }
  }, [caseId])

  useEffect(() => { loadLogs() }, [loadLogs])

  const handleLog = async (logData) => {
    try {
      await authFetch(getLocalApiUrl('/api/checks/log'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_id: caseId, ...logData, performed_by: 'web_user' }),
      })
      loadLogs()
    } catch (_) {}
  }

  const checks = Object.entries(CHECK_TYPES)

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2">
          <Search size={15} color={P.pri} />
          <span className="text-sm font-bold text-slate-800">Quick Checks</span>
          <span className="text-[11px] text-slate-400">Evidence-gathering tools</span>
        </div>
      </div>

      {/* Check buttons grid */}
      <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
        {checks.map(([key, meta]) => {
          const Icon = meta.icon
          const isActive = activeCheck === key
          return (
            <button key={key} type="button"
              onClick={() => setActiveCheck(isActive ? null : key)}
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-all cursor-pointer"
              style={{
                borderColor: isActive ? meta.color : '#e2e8f0',
                background: isActive ? meta.color + '08' : '#fff',
                borderWidth: isActive ? 2 : 1,
              }}>
              <Icon size={15} color={meta.color} className="flex-shrink-0" />
              <span className="text-xs font-semibold" style={{ color: isActive ? meta.color : '#475569' }}>{meta.label}</span>
            </button>
          )
        })}
      </div>

      {/* Active check tool */}
      {activeCheck && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3">
          <div className="flex items-center gap-2 mb-3">
            {React.createElement(CHECK_TYPES[activeCheck].icon, { size: 16, color: CHECK_TYPES[activeCheck].color })}
            <span className="text-sm font-bold" style={{ color: CHECK_TYPES[activeCheck].color }}>
              {CHECK_TYPES[activeCheck].label}
            </span>
          </div>
          {activeCheck === 'abn_lookup' ? (
            <AbnLookupTool patronName={patronName} onLog={handleLog} />
          ) : (
            <LinkOutTool checkType={activeCheck} patronName={patronName} onLog={handleLog} />
          )}
        </div>
      )}

      {/* Previous checks log */}
      {checkLogs.length > 0 && (
        <div className="border-t border-slate-100">
          <button type="button" onClick={() => setLogsExpanded(!logsExpanded)}
            className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50 text-xs font-semibold text-slate-600 border-none cursor-pointer">
            {logsExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            Previous Checks ({checkLogs.length})
          </button>
          {logsExpanded && (
            <div className="px-4 pb-3 space-y-2">
              {checkLogs.map((log, i) => {
                const meta = log.metadata ? (typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata) : {}
                const checkMeta = CHECK_TYPES[meta.check_type] || {}
                const Icon = checkMeta.icon || Search
                return (
                  <div key={log.id || i} className="flex items-start gap-2.5 py-2 border-b border-slate-50 last:border-0">
                    <Icon size={13} color={checkMeta.color || '#64748b'} className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-700">{log.title}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{log.description}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {log.created_by_name} · {log.created_at ? new Date(log.created_at).toLocaleString() : ''}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
